import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import ArrowDownRightIcon from '@/assets/ArrowDownRightIcon';
import ArrowUpRightIcon from '@/assets/ArrowUpRightIcon';

export type HeadlineWithStatCardsProps =
  SliceComponentProps<Content.HeadlineWithStatCardsSlice>;

const HeadlineWithStatCards = ({
  slice,
}: HeadlineWithStatCardsProps): JSX.Element => {
  // A more robust version
  const {
    headline = [], // Default to an empty array
    description = [], // Default to an empty array
    stat_cards = [], // Default to an empty array
  } = slice.primary || {}; // Default to an empty object

  if (slice.variation === 'noCta') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white py-8 md:py-24 pb-8 md:pb-32 bg-[#0F423A]"
      >
        <div className="container mx-auto px-4 md:px-6 flex flex-col items-start text-start gap-12">
          <div className="flex flex-col gap-6">
            <PrismicRichText
              field={headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
                ),
              }}
            />
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-white/80">{children}</p>
                ),
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {stat_cards.map((item, index) => (
              <div
                key={index}
                className="rounded-2xl p-6 flex flex-col justify-between text-left gap-12"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                <div>
                  {'card_title' in item && (
                    <PrismicRichText
                      field={item.card_title}
                      components={{
                        heading3: ({ children }) => (
                          <h3 className="text-xl font-semibold mb-4">
                            {children}
                          </h3>
                        ),
                      }}
                    />
                  )}
                  {'card_text' in item && (
                    <p className="text-white/70 max-w-[90%]">
                      {item.card_text}
                    </p>
                  )}
                </div>

                <p className="text-2xl font-bold" style={{ color: '#05F273' }}>
                  {item.stat_value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (slice.variation === 'summarized') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white py-8 md:py-12 bg-[#0F423A] overflow-hidden"
      >
        <div className="container mx-auto px-4 md:px-6 flex flex-col gap-12">
          <div className="max-w-4xl flex flex-col gap-6">
            <PrismicRichText
              field={slice.primary.headline || []}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-4xl md:text-5xl font-bold text-white">
                    {children}
                  </h2>
                ),
              }}
            />
            <PrismicRichText
              field={slice.primary.description || []}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[18px] text-white">{children}</p>
                ),
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {slice.primary.stat_cards.map((item, index) => (
              <div
                key={index}
                className="rounded-2xl p-6 flex flex-col justify-between text-left gap-1"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                <p className="text-2xl font-bold" style={{ color: '#05F273' }}>
                  {item.stat_value}
                </p>
                <p className="text-white/70 max-w-[90%]">{item.card_text}</p>
                <span className="text-md">- {item.text_from}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Default variation
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white py-24 md:py-32"
    >
      <div className="container mx-auto px-4 md:px-6 flex flex-col items-center text-center gap-12">
        <div className="max-w-4xl flex flex-col gap-6">
          <PrismicRichText
            field={headline || []}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
              ),
            }}
          />
          <PrismicRichText
            field={description || []}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-white/80">{children}</p>
              ),
            }}
          />
          <PrismicNextLink
            field={slice.primary.primary_cta}
            className="secondary-button self-center"
          >
            <ArrowRightIcon className="arrow-icon" />
            View case studies
          </PrismicNextLink>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
          {slice.primary.stat_cards.map((item, index) => (
            <div
              key={index}
              className="glassy-container rounded-2xl p-6 flex flex-col justify-between text-left"
            >
              <div>
                <PrismicRichText
                  field={item.card_title}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="text-xl font-semibold mb-4">{children}</h3>
                    ),
                  }}
                />
                <div className="flex items-center gap-4 mb-4">
                  {item.stat_trend ? (
                    <ArrowUpRightIcon className="w-8 h-8 text-green-400" />
                  ) : (
                    <ArrowDownRightIcon className="w-8 h-8 text-red-400" />
                  )}
                  <p className="text-5xl font-bold">{item.stat_value}</p>
                  <p className="text-white/70 max-w-[100px]">
                    {item.stat_caption}
                  </p>
                </div>
                <PrismicNextLink
                  field={item.details_link}
                  className="text-green-400 hover:underline"
                >
                  View details
                </PrismicNextLink>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <PrismicNextImage
                  field={item.logo_image}
                  className="h-8 object-contain self-start"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  alt={(item.logo_label || item.logo_image.alt || '') as any}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeadlineWithStatCards;
